/**
 * Der Vertrag, den jedes HUD-Teil einhält.
 *
 * Ein Teil kümmert sich um **einen** Bereich der Oberfläche und weiß nichts
 * über die anderen. `main.ts` hält nur die Liste — dadurch bleibt die
 * Spielschleife kurz, egal wie viele Anzeigen dazukommen
 * (docs/2026-07-26-ueberarbeitung.md, A5).
 */

import type { Ereignis, Schnappschuss } from '@pg/protocol';
import type { Karte } from '@pg/sim';
import type { Steuerung } from '../steuerung.js';

export interface HudUmgebung {
  steuerung: Steuerung;
  /** Karte des laufenden Abstiegs. Wechselt beim neuen Abstieg. */
  karte(): Karte;
  /** Besuchte Zellen — Grundlage für den Nebel auf der Minikarte. */
  besucht(): Uint8Array;
  /** Startet den nächsten Abstieg. */
  neuerAbstieg(): void;
}

/**
 * Ist gerade ein Overlay offen, das die Welt anhalten muss?
 *
 * Solange der Spieler eine Fähigkeit wählt oder den Abschluss liest, darf ihn
 * niemand schlagen. Im späteren Coop geht das nicht mehr — dort braucht es
 * eine andere Lösung (Zeitlimit oder Auswahl im Kontor). Solo ist Pause die
 * einzig richtige Antwort.
 */
export function overlayOffen(): boolean {
  return (
    !document.querySelector<HTMLElement>('#wahl')?.hidden ||
    !document.querySelector<HTMLElement>('#abschluss')?.hidden
  );
}

export interface HudTeil {
  name: string;
  /** Einmal beim Start bzw. nach einem neuen Abstieg. */
  bauen?(u: HudUmgebung, s: Schnappschuss): void;
  /** Jedes Bild. Muss billig sein. */
  aktualisieren?(u: HudUmgebung, s: Schnappschuss): void;
  /** Ereignisse des letzten Takts. */
  ereignis?(u: HudUmgebung, e: Ereignis, s: Schnappschuss): void;
}

/** Kleine Hilfe: Element holen oder verständlich scheitern. */
export function el<T extends Element>(auswahl: string): T {
  const e = document.querySelector<T>(auswahl);
  if (!e) throw new Error(`Element fehlt: ${auswahl}`);
  return e;
}
